/**
 * Confirmation dialog — RN counterpart of the web widget's
 * `constrained-alert-dialog.tsx`.
 *
 * The web version exists to CONSTRAIN: it portals the dialog into the widget's
 * own box so an embedded widget never dims the host page. That reasoning
 * carries over exactly — the RN widget is an overlay on someone else's app, and
 * a full-screen scrim would dim their whole UI to ask a question about ours. So
 * this fills the widget panel and nothing beyond it.
 *
 * A `<Modal>` would have been less code and is the usual RN answer, but a Modal
 * cannot be constrained: it always covers the window. The one thing it gave for
 * free — the Android back gesture — is picked up here with `BackHandler`, so
 * back still cancels rather than leaving the dialog stranded.
 *
 * The frosted overlay degrades to a flat tinted scrim. `backdrop-filter` needs
 * a native blur view to exist on RN, and 40% of the widget background — the
 * same mix the web overlay asks `color-mix` for — already reads as "dimmed, in
 * the widget's colour family".
 */

import { useEffect, useRef, type FC } from "react";
import { Animated, BackHandler, StyleSheet, Text, View } from "react-native";

import { solidColor } from "../../lib/gradient";
import { ringgId } from "../../lib/testing";
import { resolveButtonRadius, resolveFontFamily, resolveRadius, useWidgetTheme } from "../../lib/widget-theme";
import { Button } from "./button";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Web mixes the overlay to 40% of the widget background; RN layers it instead. */
const SCRIM_OPACITY = 0.4;

/** `max-w-md` — the card stops growing on tablets. */
const CARD_MAX_WIDTH = 448;

/** Web hardcodes a 16px card; the theme wins here, with that value as floor. */
const CARD_RADIUS_FALLBACK = 16;

/**
 * Web springs the card in from `scale: 0.94, y: 8` on top of the overlay fade
 * (`constrained-alert-dialog.tsx`). The Modal's own fade carries the exit, so
 * only the entrance is driven here — the card would otherwise land flat while
 * web's visibly overshoots.
 */
const CARD_SPRING = { stiffness: 420, damping: 30, mass: 1 };
const CARD_ENTER_SCALE = 0.94;
const CARD_ENTER_OFFSET = 8;

export const ConfirmDialog: FC<ConfirmDialogProps> = ({ open, title, description, confirmLabel, cancelLabel, onConfirm, onCancel }) => {
  const theme = useWidgetTheme();
  const fontFamily = resolveFontFamily(theme.fontFamily);
  const buttonRadius = resolveButtonRadius(theme.buttonStyle);
  const enter = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    // Reset on close so the next open replays the spring rather than starting
    // from wherever the last one finished.
    if (!open) {
      enter.setValue(0);
      return;
    }
    const pop = Animated.spring(enter, { toValue: 1, ...CARD_SPRING, useNativeDriver: true });
    pop.start();
    return () => pop.stop();
  }, [open, enter]);

  useEffect(() => {
    if (!open) return;
    // Android's back gesture must resolve the dialog, not fall through to the
    // host app's navigation while a confirmation is on screen.
    const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
      onCancel();
      return true;
    });
    return () => subscription.remove();
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <View style={styles.root}>
        {/* Non-interactive on purpose — the web overlay has no dismiss handler
            either, so the decision to end a call is never taken by a stray tap. */}
        <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.scrim, { backgroundColor: solidColor(theme.backgroundColor) }]} />
        <Animated.View
          testID={ringgId("end-call-dialog")}
          // Hides the rest of the tree from VoiceOver while the dialog is up.
          accessibilityViewIsModal
          style={[
            styles.card,
            {
              opacity: enter,
              transform: [{ scale: enter.interpolate({ inputRange: [0, 1], outputRange: [CARD_ENTER_SCALE, 1] }) }, { translateY: enter.interpolate({ inputRange: [0, 1], outputRange: [CARD_ENTER_OFFSET, 0] }) }],
              backgroundColor: solidColor(theme.backgroundColor),
              borderColor: solidColor(theme.borderColor),
              borderRadius: resolveRadius(theme.borderRadius, CARD_RADIUS_FALLBACK),
            },
          ]}
        >
          <View testID={ringgId("end-call-dialog-header")} style={styles.header}>
            <Text testID={ringgId("end-call-dialog-title")} style={[styles.title, { color: theme.textColor, fontFamily }]}>
              {title}
            </Text>
            {description ? (
              <Text testID={ringgId("end-call-dialog-description")} style={[styles.description, { color: theme.mutedTextColor, fontFamily }]}>
                {description}
              </Text>
            ) : null}
          </View>
          <View testID={ringgId("end-call-dialog-footer")} style={styles.footer}>
            <Button variant="outline" onPress={onCancel} testID={ringgId("end-call-cancel")} style={{ borderRadius: buttonRadius }}>
              {cancelLabel}
            </Button>
            <Button onPress={onConfirm} testID={ringgId("end-call-confirm")} style={{ borderRadius: buttonRadius }}>
              {confirmLabel}
            </Button>
          </View>
        </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Fills the widget panel rather than the window, and sits above the panel's
  // own chrome (header 10, minimize/close 20) so nothing pokes through it.
  root: { ...StyleSheet.absoluteFill, zIndex: 30, alignItems: "center", justifyContent: "center", padding: 16 },
  scrim: { opacity: SCRIM_OPACITY },
  card: {
    width: "100%",
    maxWidth: CARD_MAX_WIDTH,
    marginHorizontal: 16,
    padding: 16,
    borderWidth: 1,
    gap: 12,
    // The web card's two stacked shadows collapse into the one elevation each
    // platform can express.
    shadowColor: "#000000",
    shadowOpacity: 0.08,
    shadowRadius: 13,
    shadowOffset: { width: 0, height: 6 },
    elevation: 8,
  },
  header: { gap: 6 },
  title: { fontSize: 17, lineHeight: 24, fontWeight: "500" },
  description: { fontSize: 15, lineHeight: 21 },
  footer: { flexDirection: "row", justifyContent: "flex-end", gap: 8, paddingTop: 8 },
});
